<template>
  <div ref="rootRef" :style="{ width: '100%', display: 'flex', flexDirection: 'column', gap: '7px' }">
    <!-- Row 1: input + top-right slot -->
    <div :style="{ display: 'flex', alignItems: 'center', gap: '8px' }">
      <div :style="{ position: 'relative', flex: 1, minWidth: 0 }">
        <div
          :style="{
            display: 'flex',
            alignItems: 'center',
            gap: '9px',
            width: '100%',
            padding: '9px 12px',
            borderRadius: '12px',
            background: 'var(--p-content-background)',
            border: `1px solid ${open ? 'var(--p-primary-500)' : 'var(--p-content-border-color)'}`,
            boxShadow: open ? '0 0 0 3px color-mix(in srgb, var(--p-primary-500) 18%, transparent)' : 'none',
            transition: 'border-color .12s, box-shadow .12s',
          }"
        >
          <i class="pi pi-search" :style="{ fontSize: '13px', color: 'var(--p-surface-400)' }" />
          <input
            ref="inputRef"
            type="text"
            :value="query"
            spellcheck="false"
            :placeholder="placeholder || 'Search…'"
            :style="{ flex: 1, minWidth: 0, border: 0, outline: 0, background: 'transparent', fontSize: '13.5px', color: 'var(--p-text-color)', fontFamily: mono }"
            @input="onInput"
            @keyup="syncCaret"
            @click="syncCaret"
            @focus="open = true; syncCaret()"
            @keydown="onKeyDown"
          >
          <span
            v-if="showMatchCount"
            title="How many records match this search"
            :style="{
              flexShrink: 0,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              fontSize: '11px',
              fontWeight: 700,
              fontFamily: mono,
              color: 'var(--p-primary-700)',
              background: 'color-mix(in srgb, var(--p-primary-500) 14%, transparent)',
              borderRadius: '999px',
              padding: '2px 9px',
            }"
          >
            <span :style="{ width: '6px', height: '6px', borderRadius: '999px', background: 'var(--p-primary-500)' }" />
            {{ `${matchCount} match${matchCount === 1 ? '' : 'es'}` }}
          </span>
          <button
            v-if="query"
            title="Clear"
            :style="{ background: 'transparent', border: 0, padding: '2px', cursor: 'pointer', color: 'var(--p-surface-400)', display: 'flex' }"
            @mousedown="onClear"
          >
            <i class="pi pi-times" :style="{ fontSize: '12px' }" />
          </button>
        </div>

        <!-- popover -->
        <div
          v-if="open"
          :style="{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            zIndex: 850,
            background: 'var(--p-content-background)',
            border: '1px solid var(--p-content-border-color)',
            borderRadius: '12px',
            boxShadow: 'var(--p-overlay-popover-shadow, 0 18px 50px rgba(0,0,0,.35))',
            padding: '5px',
            maxHeight: '340px',
            overflowY: 'auto',
          }"
        >
          <div
            :style="{
              padding: '5px 10px 7px',
              fontSize: '9.5px',
              fontWeight: 800,
              letterSpacing: '.09em',
              textTransform: 'uppercase',
              color: 'var(--p-surface-400)',
              display: 'flex',
              justifyContent: 'space-between',
            }"
          >
            <span>{{ popoverTitle }}</span>
            <span v-if="ctx.mode === 'value' && sug.source === 'query'" :style="{ color: 'var(--p-surface-400)' }">backend</span>
            <span v-else-if="ctx.mode === 'value' && sug.source === 'static'" :style="{ color: 'var(--p-surface-400)' }">instant</span>
          </div>

          <!-- KEY rows -->
          <template v-if="ctx.mode === 'key'">
            <button v-for="(k, i) in keySuggestions" :key="k" :style="rowStyle(i === active)" @mouseenter="active = i" @click="applyKey(k)">
              <i :class="`pi pi-${meta(k).icon || 'tag'}`" :style="{ fontSize: '12px', color: 'var(--p-surface-400)', width: '16px' }" />
              <span :style="{ fontSize: '13px', fontWeight: 700, color: 'var(--p-text-color)', fontFamily: mono }">{{ k }}:</span>
              <span :style="{ fontSize: '12px', color: 'var(--p-surface-400)' }">{{ meta(k).label || k }}</span>
              <span
                :style="{ marginLeft: 'auto', maxWidth: '45%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '11.5px', color: 'var(--p-surface-400)' }"
              >{{ fieldHint(k) }}</span>
            </button>
            <Empty v-if="keySuggestions.length === 0" label="No matching fields" />
          </template>

          <!-- VALUE rows + async states -->
          <template v-if="ctx.mode === 'value' && fieldType === 'boolean'">
            <button v-for="(v, i) in boolValues" :key="v" :style="rowStyle(i === active)" @mouseenter="active = i" @click="applyValue(v)">
              <i :class="`pi pi-${v === 'true' ? 'check-circle' : 'times-circle'}`" :style="{ fontSize: '13px', color: v === 'true' ? 'var(--p-primary-color)' : 'var(--p-surface-400)' }" />
              <span :style="{ fontSize: '13px', color: 'var(--p-text-color)', fontFamily: mono }">{{ v }}</span>
            </button>
          </template>
          <Empty v-if="ctx.mode === 'value' && fieldType === 'date'" label="Pick dates in the Builder →" />
          <Empty v-if="ctx.mode === 'value' && fieldType === 'objectId'" label="Type an exact id, then a space" />
          <LoadingRows v-if="ctx.mode === 'value' && sug.status === 'loading'" />
          <div v-if="ctx.mode === 'value' && sug.status === 'error'" :style="{ padding: '12px 12px 10px', display: 'flex', alignItems: 'center', gap: '10px' }">
            <i class="pi pi-exclamation-triangle" :style="{ color: 'var(--p-status-danger)', fontSize: '13px' }" />
            <span :style="{ fontSize: '12.5px', color: 'var(--p-text-muted-color)', flex: 1 }">{{ sug.error || 'Couldn’t load suggestions.' }}</span>
            <button
              :style="{ fontSize: '11.5px', fontWeight: 700, color: 'var(--p-primary-700)', background: 'transparent', border: '1px solid var(--p-content-border-color)', borderRadius: '7px', padding: '4px 10px', cursor: 'pointer', fontFamily: 'inherit' }"
              @click="sug.retry"
            >Retry</button>
          </div>
          <Empty
            v-if="ctx.mode === 'value' && sug.status === 'empty'"
            :label="ctx.partial ? `No values matching “${ctx.partial}”` : 'No suggestions for this field'"
          />
          <template v-if="ctx.mode === 'value' && sug.status === 'ready'">
            <button v-for="(v, i) in sug.values" :key="v" :style="rowStyle(i === active)" @mouseenter="active = i" @click="applyValue(v)">
              <Check :on="chosenSet.has(v)" />
              <span
                v-if="enumMetaFor(ctx.field, v) && enumMetaFor(ctx.field, v).color"
                :style="{ width: '8px', height: '8px', borderRadius: '999px', background: enumMetaFor(ctx.field, v).color, flexShrink: 0 }"
              />
              <i
                v-if="enumMetaFor(ctx.field, v) && enumMetaFor(ctx.field, v).icon"
                :class="`pi pi-${enumMetaFor(ctx.field, v).icon}`"
                :style="{ fontSize: '11px', color: enumMetaFor(ctx.field, v).color || 'var(--p-surface-400)' }"
              />
              <span :style="{ fontSize: '13px', color: 'var(--p-text-color)', fontFamily: mono }">
                {{ (enumMetaFor(ctx.field, v) && enumMetaFor(ctx.field, v).label) || v }}
              </span>
            </button>
          </template>
          <Empty v-if="ctx.mode === 'value-unknown'" :label="`“${ctx.field}” isn’t a known field`" />
          <Empty v-if="ctx.mode === 'range'" label="Editing a range — use the sidebar for bounds" />
        </div>
      </div>
      <div v-if="$slots.topRight" :style="{ flexShrink: 0, display: 'flex', alignItems: 'center' }"><slot name="topRight" /></div>
    </div>

    <!-- Row 2: did-you-mean / hints / help + bottom-right slot -->
    <div
      v-if="showRow2"
      :style="{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'space-between', minHeight: '18px' }"
    >
      <div :style="{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }">
        <span
          v-if="corrections.length === 0 && hints.length === 0 && helpText"
          :style="{ fontSize: '11.5px', color: 'var(--p-surface-400)', display: 'inline-flex', alignItems: 'center', gap: '6px' }"
        >
          <i class="pi pi-info-circle" :style="{ fontSize: '11px' }" />{{ helpText }}
        </span>
        <div v-else :style="{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', minWidth: 0 }">
          <button
            v-for="(c, i) in corrections"
            :key="'c' + i"
            :style="{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              border: '1px solid color-mix(in srgb, var(--p-status-warning) 50%, transparent)',
              background: 'color-mix(in srgb, var(--p-status-warning) 14%, transparent)',
              color: 'var(--p-text-color)',
              borderRadius: '999px',
              padding: '4px 11px',
              fontFamily: 'inherit',
              fontSize: '12px',
            }"
            @click="applyCorrection(c)"
          >
            <i class="pi pi-bolt" :style="{ fontSize: '11px', color: 'var(--p-status-warning)' }" />
            <span :style="{ color: 'var(--p-surface-400)' }">Did you mean</span>
            <span :style="{ fontFamily: mono, fontWeight: 700 }">{{ c.label }}</span>
            <span :style="{ fontFamily: mono, color: 'var(--p-surface-400)', textDecoration: 'line-through' }">{{ c.from }}</span>
          </button>
          <span
            v-for="(d, i) in hints"
            :key="'h' + i"
            :title="d.code"
            :style="{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11.5px', color: d.severity === 'error' ? 'var(--p-status-danger)' : 'var(--p-text-muted-color)' }"
          >
            <i :class="`pi ${d.severity === 'error' ? 'pi-exclamation-circle' : 'pi-info-circle'}`" :style="{ fontSize: '11px' }" />
            {{ humanize(d) }}
          </span>
        </div>
      </div>
      <div v-if="$slots.bottomRight" :style="{ flexShrink: 0, display: 'flex', alignItems: 'center' }"><slot name="bottomRight" /></div>
    </div>
  </div>
</template>

<script setup>
// ── FQL SearchBar ────────────────────────────────────────────────────────
// The structured-search input: token-aware typeahead (fields → values),
// multi-select comma lists, did-you-mean chips, plain-language hints. Ported 1:1
// from fql-search.jsx SearchBar. Reads/writes the single query string through
// `search` (from useStructuredSearch).
import { ref, computed, watch, nextTick, onBeforeUnmount, useSlots } from 'vue'
import { FqlUtilities } from '@frankai/fql'
import * as H from '@/admin-collection-layout/composables/fqlSearchHelpers.js'
import { useValueSuggestions } from '@/admin-collection-layout/composables/useStructuredSearch.js'
import { mono, rowStyle, Check, Empty, LoadingRows } from './search/bits.js'

const props = defineProps({
  search: { type: Object, required: true },
  fieldDefinitions: { type: Object, required: true },
  fieldMeta: { type: Object, default: () => ({}) },
  valueProvider: { type: Function, default: null },
  knownValues: { type: Object, default: null },
  suggestionLimit: { type: Number, default: 8 },
  placeholder: { type: String, default: null },
  showMatchCount: { type: Boolean, default: true },
  debounceMs: { type: Number, default: 220 },
  helpText: { type: String, default: null },
  // How many records the whole search matches, when the rows held are one page
  // of it. Without it the chip counts the rows held.
  totalCount: { type: Number, default: null },
})

// completion-position resolution — decide KEY vs VALUE under the caret and
// expose the multi-select segments so comma lists round-trip.
function resolveCompletion(query, caret, fieldDefs, knownValues) {
  const left = query.slice(0, caret)
  let b = -1
  for (const ch of [' ', '(', ')']) b = Math.max(b, left.lastIndexOf(ch))
  const tokenStart = b + 1
  const token = query.slice(tokenStart, caret)
  const colon = token.indexOf(':')
  if (colon === -1) {
    const before = query.slice(0, tokenStart)
    const m = /([A-Za-z_][\w.]*):\s*$/.exec(before)
    if (m && fieldDefs.fields && fieldDefs.fields[m[1]] && (m.index === 0 || /[\s()]/.test(query[m.index - 1]))) {
      return { mode: 'value', tokenStart: m.index, field: m[1], partial: token, chosen: [], known: true }
    }
    return { mode: 'key', tokenStart, partial: token }
  }
  const field = token.slice(0, colon)
  const rest = token.slice(colon + 1)
  if (rest[0] === '[' || rest[0] === '{') return { mode: 'range', tokenStart, field }
  const decl = (fieldDefs.fields && fieldDefs.fields[field]) || null
  const segs = rest.split(',')
  let partial = segs[segs.length - 1]
  const chosen = segs.slice(0, -1).map((s) => s.trim()).filter(Boolean)
  const knownSet = decl ? new Set([...(decl.values || []), ...((knownValues && knownValues[field]) || [])]) : null
  if (knownSet && partial && knownSet.has(partial)) {
    chosen.push(partial.trim())
    partial = ''
  }
  return { mode: decl ? 'value' : 'value-unknown', tokenStart, field, partial, chosen, known: !!decl }
}

const open = ref(false)
const active = ref(0)
const caret = ref(props.search.query.length)
const rootRef = ref(null)
const inputRef = ref(null)
const pendingCaret = ref(null)

const query = computed(() => props.search.query)
const ctx = computed(() => resolveCompletion(query.value, caret.value, props.fieldDefinitions, props.knownValues))
// Fields can opt out of the search UI via fieldMeta[key].searchHidden (kept in
// fieldDefinitions for display, just not offered in typeahead).
const fieldNames = computed(() => Object.keys(props.fieldDefinitions.fields || {}).filter((k) => !props.fieldMeta[k]?.searchHidden))
const meta = (k) => props.fieldMeta[k] || {}

const keySuggestions = computed(() => {
  if (ctx.value.mode !== 'key') return []
  const p = (ctx.value.partial || '').toLowerCase()
  return fieldNames.value
    .filter((k) => !p || k.toLowerCase().startsWith(p) || ((meta(k).label || '').toLowerCase().includes(p)))
    .slice(0, 10)
})

const fieldType = computed(() => (props.fieldDefinitions.fields[ctx.value.field] || {}).type)
const valueEnabled = computed(
  () => open.value && ctx.value.mode === 'value' && fieldType.value !== 'boolean' && fieldType.value !== 'date' && fieldType.value !== 'objectId',
)
const sug = useValueSuggestions({
  field: () => (valueEnabled.value ? ctx.value.field : null),
  prefix: () => (valueEnabled.value ? ctx.value.partial : ''),
  fieldDefinitions: () => props.fieldDefinitions,
  valueProvider: props.valueProvider,
  limit: () => props.suggestionLimit,
  enabled: () => valueEnabled.value,
  debounceMs: () => props.debounceMs,
})
const chosenSet = computed(() => new Set(ctx.value.chosen || []))

const corrections = computed(() => H.didYouMean(query.value, props.search.ast, props.search.diagnostics, props.fieldDefinitions, props.knownValues))
const hints = computed(() => {
  if (open.value) return []
  const fixed = new Set(corrections.value.map((c) => c.span.start + '-' + c.span.end))
  return props.search.diagnostics.filter((d) => d.span && !fixed.has(d.span.start + '-' + d.span.end) && d.code !== 'FQL-CLAUSE-SKIPPED')
})

const firstField = computed(() => Object.keys(props.fieldDefinitions.fields || {})[0] || 'make')
function humanize(d) {
  const seg = (d.span ? query.value.slice(d.span.start, d.span.end) : '').trim()
  const ci = seg.indexOf(':')
  const field = ci > 0 ? seg.slice(0, ci) : null
  const val = ci >= 0 ? seg.slice(ci + 1) : seg
  const lbl = (f) => (f ? meta(f).label || f : 'this field')
  switch (d.code) {
    case 'FQL-BARE-TERM':
      return `Add a field in front of “${val || seg}” — e.g. ${firstField.value}:${val || '…'}`
    case 'FQL-INCOMPLETE-CLAUSE':
      return `Pick a value for ${lbl(field)}.`
    case 'FQL-VALUE-NOT-IN-ENUM':
      return `“${val}” isn’t a ${lbl(field)} option — choose one from the list.`
    case 'FQL-COERCION-FAILED':
      return `${lbl(field)} takes a number — “${val}” isn’t one.`
    case 'FQL-UNKNOWN-FIELD':
      return `There’s no “${field || val}” to search by.`
    case 'FQL-WILDCARD-NOT-ALLOWED':
      return `“${lbl(field)}” is pick-from-a-list — the * wildcard only works on free-text fields.`
    default:
      return d.message
  }
}

function closeAndNormalize() {
  open.value = false
  try {
    const canon = FqlUtilities.serialize(props.search.ast)
    if (canon && canon !== query.value) {
      pendingCaret.value = null
      props.search.setQuery(canon)
    }
  } catch {
    /* leave the raw string untouched if it won't serialize */
  }
}

watch([() => ctx.value.mode, () => ctx.value.field, () => ctx.value.partial], () => {
  active.value = 0
})

function onDocDown(e) {
  if (rootRef.value && !rootRef.value.contains(e.target)) closeAndNormalize()
}
watch(open, (v) => {
  if (v) document.addEventListener('mousedown', onDocDown)
  else document.removeEventListener('mousedown', onDocDown)
})
onBeforeUnmount(() => document.removeEventListener('mousedown', onDocDown))

// Re-apply caret after a programmatic edit.
watch(query, () => {
  if (pendingCaret.value != null && inputRef.value) {
    nextTick(() => {
      if (pendingCaret.value == null || !inputRef.value) return
      inputRef.value.focus()
      inputRef.value.setSelectionRange(pendingCaret.value, pendingCaret.value)
      caret.value = pendingCaret.value
      pendingCaret.value = null
    })
  }
})

function commit(nextQuery, nextCaret, keepOpen = true) {
  pendingCaret.value = nextCaret
  props.search.setQuery(nextQuery)
  open.value = keepOpen
}
function applyKey(key) {
  const ins = key + ':'
  const next = query.value.slice(0, ctx.value.tokenStart) + ins + query.value.slice(caret.value)
  commit(next, ctx.value.tokenStart + ins.length)
}
function applyValue(val, close = false) {
  if (fieldType.value === 'boolean') {
    const tok = ctx.value.field + ':' + val + ' '
    const next = query.value.slice(0, ctx.value.tokenStart) + tok + query.value.slice(caret.value)
    commit(next, ctx.value.tokenStart + tok.length, !close)
    return
  }
  const set = new Set(ctx.value.chosen || [])
  if (set.has(val)) set.delete(val)
  else set.add(val)
  const vals = [...set]
  const tok = ctx.value.field + ':' + vals.join(',')
  const next = query.value.slice(0, ctx.value.tokenStart) + tok + query.value.slice(caret.value)
  commit(next, ctx.value.tokenStart + tok.length, !close)
}

const boolValues = computed(() => (fieldType.value === 'boolean' ? ['true', 'false'] : null))
const visibleList = computed(() =>
  ctx.value.mode === 'key' ? keySuggestions.value : ctx.value.mode === 'value' ? boolValues.value || sug.values : [],
)

function onKeyDown(e) {
  if (!open.value) return
  const len = visibleList.value.length
  if (e.key === 'ArrowDown' && len) {
    e.preventDefault()
    active.value = (active.value + 1) % len
  } else if (e.key === 'ArrowUp' && len) {
    e.preventDefault()
    active.value = (active.value - 1 + len) % len
  } else if (e.key === 'Enter') {
    e.preventDefault()
    if (len && ctx.value.mode === 'key') applyKey(visibleList.value[active.value])
    else if (len && ctx.value.mode === 'value') applyValue(visibleList.value[active.value], true)
    else closeAndNormalize()
  } else if (e.key === 'Escape') closeAndNormalize()
}

function syncCaret() {
  if (inputRef.value) caret.value = inputRef.value.selectionStart ?? query.value.length
}
function onInput(e) {
  props.search.setQuery(e.target.value)
  pendingCaret.value = null
  requestAnimationFrame(syncCaret)
  open.value = true
}
function onClear(e) {
  e.preventDefault()
  props.search.setQuery('')
  open.value = false
}
function applyCorrection(c) {
  const next = H.replaceSpan(query.value, c.span.start, c.span.end, c.to)
  pendingCaret.value = c.span.start + c.to.length
  props.search.setQuery(next)
}

const enumMetaFor = (field, value) => {
  const m = meta(field).enumMeta
  return (m && m[value]) || null
}
function fieldHint(k) {
  const decl = props.fieldDefinitions.fields[k] || {}
  const m = meta(k)
  if (m.example) return m.example
  if (decl.type === 'enum' && (decl.values || []).length) {
    const vals = decl.values.map((v) => (m.enumMeta && m.enumMeta[v] && m.enumMeta[v].label) || v)
    return vals.slice(0, 3).join(', ') + (vals.length > 3 ? `, +${vals.length - 3}` : '')
  }
  if (decl.type === 'boolean') return 'yes / no'
  if (decl.type === 'date') return 'e.g. 2024-08-01'
  if (decl.type === 'number') return 'e.g. 2020'
  if (decl.type === 'objectId') return 'an exact ID'
  if (decl.type === 'string' && decl.match === 'contains') return 'any text it contains'
  const kv = props.knownValues && props.knownValues[k]
  if (kv && kv.length) return `e.g. ${kv[0]}`
  return 'a value'
}

const matchCount = computed(() => props.totalCount ?? props.search.matchCount)
const popoverTitle = computed(() =>
  ctx.value.mode === 'key' ? 'Filter by field' : ctx.value.mode === 'value' ? `Values · ${ctx.value.field}` : ctx.value.mode === 'value-unknown' ? 'Unknown field' : 'Range',
)
const slots = useSlots()
const showRow2 = computed(() => corrections.value.length > 0 || hints.value.length > 0 || props.helpText || !!slots.bottomRight)
</script>
